import './health-profile-form.scss';
import {useEffect, useState, useContext} from 'react';
import {Box, Card, Checkbox, Typography} from '@mui/material';
import {translations} from '../../i18n/translations';
import {HealthProfile} from '../../backend/interfaces/health-profile';
import {useTranslation} from 'react-i18next';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {UsersRepository} from '../../repositories/users';
import {PatchHealthProfile} from '../../backend/requests/patch-health-profile';
import {ContentPage} from '../content-page/content-page';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {ErrorBox} from '../error-box/error-box';
import {LoadingContent} from '../loading-content/loading-content';
import {
  BloodGlucoseHealthMeasurementData,
  BloodOxygenHealthMeasurementData,
  BloodPressureHealthMeasurementData,
  BodyFatPercentageHealthMeasurementData,
  BodyHeightHealthMeasurementData,
  BodyMusclePercentageHealthMeasurementData,
  BodyTemperatureHealthMeasurementData,
  BodyWaterPercentageHealthMeasurementData,
  BodyWeightHealthMeasurementData,
  HealthMeasurement,
  HeartRateHealthMeasurementData,
  NatriureticPeptideTestHealthMeasurementData,
  RespiratoryRateHealthMeasurementData,
  SleepHealthMeasurementData,
  StepsHealthMeasurementData,
} from '../../backend/interfaces/health-measurement';
import {HealthMeasurementType} from '../../backend/enums/health-measurement-type';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import AirIcon from '@mui/icons-material/Air';
import moment from 'moment';
import HopeCareLogo from '../../logos/hope-care.png';
import AlticeSmartAlLogo from '../../logos/altice-smart-al.png';
import {HealthEnablementProvider} from '../../backend/enums/health-enablement-provider';
import {HealthMeasurementConsideration} from '../../backend/enums/health-measurement-consideration';
import OpacityIcon from '@mui/icons-material/Opacity';
import HotelIcon from '@mui/icons-material/Hotel';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import HeightIcon from '@mui/icons-material/Height';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faHeartPulse,
  faHeartCrack,
  faCookieBite,
  faWeightHanging,
  faDumbbell,
  faHeartCircleBolt,
  faShoePrints,
} from '@fortawesome/free-solid-svg-icons';
import {Tooltip} from '../tooltip/tooltip';
import {EntityActivitiesRepository} from '../../repositories/entity-activities';
import {CLINICAL_SUPPORT_SECTOR_ID, CLINICAL_SUPPORT__VITAL_SIGNS_ID} from '../../utils/constants';
import {useParams} from 'react-router-dom';
import {ActivityType} from '../../backend/interfaces/activity-type';

/**
 * Health profile of a senior.
 *
 * This form was created taking into account that it will exist under the senior tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const HealthProfileForm = (props: {userId: string; canWrite: boolean}) => {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Translations
  const {t} = useTranslation();
  const {entityId}: {entityId: string} = useParams();

  // Hold state
  const [sortedClinicalConditions] = useState<
    Array<{
      key: keyof HealthProfile;
      label: string;
    }>
  >(
    [
      {key: 'hasAlzheimer', label: t(translations.health_profile_form.alzheimer_label)},
      {
        key: 'hasCognitiveDeficit',
        label: t(translations.health_profile_form.cognitive_deficit_label),
      },
      {
        key: 'hasHearingDificulty',
        label: t(translations.health_profile_form.hearing_difficulty_label),
      },
      {key: 'hasOtherDementias', label: t(translations.health_profile_form.other_dementias_label)},
      {
        key: 'hasParalysisLeftLimb',
        label: t(translations.health_profile_form.paralysis_left_limb_label),
      },
      {
        key: 'hasParalysisRightLimb',
        label: t(translations.health_profile_form.paralysis_right_limb_label),
      },
      {key: 'hasParkinson', label: t(translations.health_profile_form.parkinson_label)},
      {
        key: 'hasReducedMobilityLowerLimbs',
        label: t(translations.health_profile_form.reduced_mobility_lower_limbs_label),
      },
      {
        key: 'hasReducedMobilityUpperLimbs',
        label: t(translations.health_profile_form.reduced_mobility_upper_limbs_label),
      },
      {
        key: 'hasVisualDificulty',
        label: t(translations.health_profile_form.visual_difficulty_label),
      },
      {key: 'isIlliterate', label: t(translations.health_profile_form.illiterate_label)},
      {key: 'usesWheelchair', label: t(translations.health_profile_form.wheelchair_label)},
    ].sort((a, b) =>
      a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase())
    ) as Array<{
      key: keyof HealthProfile;
      label: string;
    }>
  );
  const [healthProfileLoading, setHealthProfileLoading] = useState<boolean>(true);
  const [healthProfileError, setHealthProfileError] = useState<string | undefined>();
  const defaultHealthProfile: HealthProfile = {
    userId: props.userId,
  };
  const [healthProfile, setHealthProfile] = useState<HealthProfile>(defaultHealthProfile);

  const [healthMeasurementsLoading, setHealthMeasurementsLoading] = useState<boolean>(true);
  const [healthMeasurementsError, setHealthMeasurementsError] = useState<string | undefined>();
  const [healthMeasurements, setHealthMeasurements] = useState<
    Array<HealthMeasurement> | null | undefined
  >(undefined);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);

  // Get health profile
  useEffect(() => {
    // Get health profile
    const getHealthProfile = async () => {
      try {
        // Start loading
        setHealthProfileLoading(true);

        // Request backend
        const response: HealthProfile | null = await UsersRepository.instance.getUserHealthProfile(
          props.userId
        );

        // Set health profile
        if (response) {
          setHealthProfile(response);
        } else {
          setHealthProfile(defaultHealthProfile);
        }
      } catch (error: any) {
        // Log
        console.debug('🔴 Could not get user health profile.', {seniorId: props.userId, error});

        // Set error
        setHealthProfileError(error.message);
      } finally {
        // Stop loading
        setHealthProfileLoading(false);
      }
    };

    // Get health measurements
    const getHealthMeasurements = async () => {
      try {
        // Start loading
        setHealthMeasurementsLoading(true);

        // Request backend
        const healthMeasuresProm = UsersRepository.instance.getUserHealthMeasurements(props.userId);

        const activityTypesProm = EntityActivitiesRepository.instance.getTypes(
          0,
          100,
          entityId,
          CLINICAL_SUPPORT_SECTOR_ID,
          CLINICAL_SUPPORT__VITAL_SIGNS_ID
        );

        const [response, activityTypesRes] = await Promise.all([
          healthMeasuresProm,
          activityTypesProm,
        ]);

        // Set health profile
        if (response) {
          setHealthMeasurements(response);
        } else {
          setHealthMeasurements(null);
        }
        // Set activity types
        setActivityTypes(activityTypesRes.data);
      } catch (error: any) {
        // Log
        console.debug('🔴 Could not get user health measurements.', {
          seniorId: props.userId,
          error,
        });

        // Set error
        setHealthMeasurementsError(error.message);
      } finally {
        // Stop loading
        setHealthMeasurementsLoading(false);
      }
    };

    // Init
    getHealthProfile();
    getHealthMeasurements();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userId]);

  // Update health profile
  const handleUpdateHealthProfile = async (partialBody: Partial<PatchHealthProfile>) => {
    // Current health profile
    const currentHealthProfile: HealthProfile = healthProfile;

    // Update state and UI
    setHealthProfile((prevState) => {
      return {
        ...prevState,
        ...partialBody,
      };
    });

    // Make request to the backend
    // Don't wait to speed up the UI
    handleContinueUpdateHealthProfile(currentHealthProfile, partialBody);
  };

  // Update health profile
  const handleContinueUpdateHealthProfile = async (
    currentHealthProfile: HealthProfile,
    partialBody: Partial<PatchHealthProfile>
  ) => {
    try {
      // Default request body
      const defaultRequestBody: PatchHealthProfile = {
        hasVisualDificulty: currentHealthProfile.hasVisualDificulty === true,
        hasAlzheimer: currentHealthProfile.hasAlzheimer === true,
        hasHearingDificulty: currentHealthProfile.hasHearingDificulty === true,
        hasCognitiveDeficit: currentHealthProfile.hasCognitiveDeficit === true,
        hasParkinson: currentHealthProfile.hasParkinson === true,
        hasOtherDementias: currentHealthProfile.hasOtherDementias === true,
        hasReducedMobilityUpperLimbs: currentHealthProfile.hasReducedMobilityUpperLimbs === true,
        hasReducedMobilityLowerLimbs: currentHealthProfile.hasReducedMobilityLowerLimbs === true,
        hasParalysisLeftLimb: currentHealthProfile.hasParalysisLeftLimb === true,
        hasParalysisRightLimb: currentHealthProfile.hasParalysisRightLimb === true,
        usesWheelchair: currentHealthProfile.usesWheelchair === true,
        isIlliterate: currentHealthProfile.isIlliterate === true,
      };

      // Request body
      const requestBody: PatchHealthProfile = {
        ...defaultRequestBody,
        ...partialBody,
      };

      // Make request to the backend
      const response: HealthProfile = await UsersRepository.instance.updateUserHealthProfile(
        props.userId,
        requestBody
      );

      // Update state
      setHealthProfile(response);

      // Log
      console.debug('🟢 User health profile updated.', {
        seniorId: props.userId,
        partialBody,
        error: healthProfileError,
      });
    } catch (error) {
      // Undo partial body
      let partialBodyUndone: Partial<PatchHealthProfile> = {};
      for (let key in partialBody) {
        (partialBodyUndone as any)[key] = !(partialBody as any)[key];
      }

      // Undo state and UI update
      setHealthProfile((prevState) => {
        return {
          ...prevState,
          ...partialBodyUndone,
        };
      });

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.health_profile_form.errors.update_health_profile_error_message),
      });

      // Log
      console.debug('🔴 Could not update the user health profile.', {
        seniorId: props.userId,
        partialBody,
        error,
      });
    }
  };

  // Condition component
  const renderCondition = (title: string, checked: boolean, handleClick: () => Promise<void>) => (
    <Box key={`health-profile-form-${title}`} className="health-profile-form-condition-container">
      <Tooltip.NotAllowedIf
        condition={!props.canWrite}
        title={t(translations.health_profile_form.disable_clinical_conditions_tooltip)}
      >
        <Checkbox checked={checked} onClick={handleClick} disabled={!props.canWrite} />
      </Tooltip.NotAllowedIf>
      <Typography variant="body2" component="span" fontWeight={checked ? 'bold' : 'normal'}>
        {title}
      </Typography>
    </Box>
  );

  // Measurement component
  const renderMeasurement = (measurement: HealthMeasurement) => {
    // Define Color
    let color: string | undefined = undefined;

    // Define Icon
    let MeasurementIcon: any | undefined = undefined;

    // Define Value Label
    let valueLabel: string = '';

    // Define Name
    let name: string = '';

    // Define Provider logo
    let providerLogo: any | undefined = undefined;

    if (!measurement.data) {
      console.error('Measurement data not exists.', measurement);
      return;
    }

    const activityTypeName = activityTypes.find(
      (type) => type.healthMeasurementType === measurement.type
    )?.name;

    // Name
    name = activityTypeName ?? measurement.type;

    // Eval type
    switch (measurement.type) {
      case HealthMeasurementType.BodyTemperature:
        // Cast Data
        const bodyTemperatureData: BodyTemperatureHealthMeasurementData =
          measurement.data as BodyTemperatureHealthMeasurementData;

        // Icon
        MeasurementIcon = <DeviceThermostatIcon fontSize="medium" />;

        // Label
        valueLabel = `${bodyTemperatureData.temperatureInCelsius} ºC`;

        break;
      case HealthMeasurementType.BodyWeight:
        // Cast Data
        const bodyWeightData: BodyWeightHealthMeasurementData =
          measurement.data as BodyWeightHealthMeasurementData;

        // Icon
        MeasurementIcon = <MonitorWeightIcon fontSize="medium" />;

        // Label
        valueLabel = `${bodyWeightData.weightInKilograms.toFixed(1)} Kg`;

        break;
      case HealthMeasurementType.BodyHeight:
        // Cast Data
        const bodyHeightData: BodyHeightHealthMeasurementData =
          measurement.data as BodyHeightHealthMeasurementData;

        // Icon
        MeasurementIcon = <HeightIcon fontSize="medium" />;

        // Label
        valueLabel = `${bodyHeightData.heightInCentimeters} cm`;

        break;
      case HealthMeasurementType.HeartRate:
        // Cast Data
        const heartRateData: HeartRateHealthMeasurementData =
          measurement.data as HeartRateHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faHeartPulse} />;

        // Label
        valueLabel = `${heartRateData.beatsPerMinute} ${t(
          translations.health_measurements.units.beatsPerMinute
        )}`;

        break;
      case HealthMeasurementType.BloodOxygen:
        // Cast Data
        const bloodOxygenData: BloodOxygenHealthMeasurementData =
          measurement.data as BloodOxygenHealthMeasurementData;

        // Icon
        MeasurementIcon = <AirIcon fontSize="medium" />;

        // Label
        valueLabel = `${bloodOxygenData?.percentage || 0} %`;

        break;
      case HealthMeasurementType.BloodPressure:
        // Cast Data
        const bloodPressureData: BloodPressureHealthMeasurementData =
          measurement.data as BloodPressureHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faHeartCrack} />;

        // Label
        valueLabel = `${bloodPressureData.systolic}/${bloodPressureData.diastolic} mmHg`;

        break;
      case HealthMeasurementType.RespiratoryRate:
        // Cast Data
        const respiratoryRateData: RespiratoryRateHealthMeasurementData =
          measurement.data as RespiratoryRateHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faHeartCrack} />;

        // Label
        valueLabel = `${respiratoryRateData.breathsPerMinute} ${t(
          translations.health_measurements.units.breathsPerMinute
        )}`;

        break;
      case HealthMeasurementType.BloodGlucose:
        // Cast Data
        const bloodGlucoseData: BloodGlucoseHealthMeasurementData =
          measurement.data as BloodGlucoseHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faCookieBite} />;

        // Label
        valueLabel = `${bloodGlucoseData.value} mg/dL`;

        break;
      case HealthMeasurementType.BodyFatPercentage:
        // Cast Data
        const bodyFatPercentageData: BodyFatPercentageHealthMeasurementData =
          measurement.data as BodyFatPercentageHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faWeightHanging} />;

        // Label
        valueLabel = `${bodyFatPercentageData?.percentage || 0} %`;

        break;
      case HealthMeasurementType.BodyMusclePercentage:
        // Cast Data
        const bodyMusclePercentageData: BodyMusclePercentageHealthMeasurementData =
          measurement.data as BodyMusclePercentageHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faDumbbell} />;

        // Label
        valueLabel = `${bodyMusclePercentageData?.percentage || 0} %`;

        break;
      case HealthMeasurementType.BodyWaterPercentage:
        // Cast Data
        const bodyWaterPercentageData: BodyWaterPercentageHealthMeasurementData =
          measurement.data as BodyWaterPercentageHealthMeasurementData;

        // Icon
        MeasurementIcon = <OpacityIcon fontSize="medium" />;

        // Label
        valueLabel = `${bodyWaterPercentageData?.percentage || 0} %`;

        break;
      case HealthMeasurementType.NatriureticPeptideTest:
        // Cast Data
        const natriureticPeptideTestData: NatriureticPeptideTestHealthMeasurementData =
          measurement.data as NatriureticPeptideTestHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faHeartCircleBolt} />;

        // Text
        if (natriureticPeptideTestData.bnp) {
          // Label
          valueLabel = `${natriureticPeptideTestData.bnp} pg/ml`;
        } else if (natriureticPeptideTestData.nt_proBnp) {
          // Label
          valueLabel = `${natriureticPeptideTestData.nt_proBnp} pg/ml`;
        }
        break;
      case HealthMeasurementType.Steps:
        // Cast Data
        const stepsData: StepsHealthMeasurementData =
          measurement.data as StepsHealthMeasurementData;

        // Icon
        MeasurementIcon = <FontAwesomeIcon icon={faShoePrints} />;

        // Label
        valueLabel = `${stepsData.value}`;

        break;
      case HealthMeasurementType.Sleep:
        // Cast Data
        const sleepData: SleepHealthMeasurementData =
          measurement.data as SleepHealthMeasurementData;

        // Icon
        MeasurementIcon = <HotelIcon fontSize="medium" />;

        // Label
        valueLabel = `${(sleepData.value / 60).toFixed(1)} h`;

        break;
    }

    // Provider logo
    if (measurement.provider === HealthEnablementProvider.PartnerP3N1) {
      // Hope Care
      providerLogo = <img src={HopeCareLogo} alt="Hope Care" style={{maxWidth: '100%'}} />;
    } else if (measurement.provider === HealthEnablementProvider.PartnerP3N2) {
      // Altice SmartAL
      providerLogo = (
        <img src={AlticeSmartAlLogo} alt="Altice SmartAL" style={{maxWidth: '100%'}} />
      );
    }

    // Color
    switch (measurement.consideration) {
      case HealthMeasurementConsideration.Critical:
        color = '#EA212D';
        break;
      case HealthMeasurementConsideration.Serious:
        color = '#F59231';
        break;
      case HealthMeasurementConsideration.Good:
        color = '#40B44F';
        break;
      default:
        color = '#118898';
        break;
    }

    return (
      <Card
        key={`measurement-${measurement.id || measurement._id}`}
        sx={{
          width: '296px',
          height: '168px',
          borderRadius: '16px',
          margin: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
          backgroundColor: color,
          boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.30)',
          color: '#FFFFFF',
        }}
      >
        <Box
          className="name"
          sx={{
            wordWrap: 'break-word',
            width: '100%',
            textAlign: 'center',
            paddingLeft: '12px',
            paddingRight: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#E5E5E5',
            color: '#118898',
            minHeight: '74px',
            fontSize: '18px',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          }}
        >
          {name}
        </Box>
        <Box
          className="value"
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#FFFFFF',
            color: '#118898',
            width: '100%',
            fontSize: '16px',
          }}
        >
          <Box className="icon" sx={{marginRight: '4px'}}>
            {MeasurementIcon}
          </Box>
          <Box className="label" sx={{fontSize: '24px'}}>
            {valueLabel}
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            color: '#118898',
            width: '100%',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            fontSize: '14px',
          }}
        >
          <Box
            className="date-time"
            sx={{
              // fontSize: '18px',
              textAlign: 'center',
              paddingBottom: '8px',
            }}
            title={moment(measurement.endedAt).format('LLL')}
          >
            {moment(measurement.endedAt).fromNow()}
          </Box>
          <Box sx={{maxWidth: '100px', marginTop: !!providerLogo ? '6px' : '0'}}>
            {providerLogo}
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <ContentPage>
      <Box className="health-profile-form-container">
        <Typography className={'health-profile-form-section'} noWrap>
          {t(translations.health_measurements.title)}
        </Typography>
        {healthMeasurementsLoading && <LoadingContent />}
        {healthMeasurementsError && !healthMeasurementsLoading && (
          <ErrorBox error={healthMeasurementsError} />
        )}
        {!healthMeasurementsError &&
          !healthMeasurementsLoading &&
          healthMeasurements &&
          healthMeasurements.length > 0 && (
            <Box
              className="health-measurements-container"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {healthMeasurements.map((measurement) => {
                return renderMeasurement(measurement);
              })}
            </Box>
          )}
        {!healthMeasurementsError &&
          !healthMeasurementsLoading &&
          healthMeasurements &&
          healthMeasurements.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <p>{t(translations.health_profile_form.noHealthMeasurements)}</p>
            </Box>
          )}
      </Box>
      <br />
      <br />

      <Box className="health-profile-form-container">
        <Typography className={'health-profile-form-section'} noWrap>
          {t(translations.health_profile_form.clinical_conditions_title)}
        </Typography>
        {healthProfileLoading && <LoadingContent />}
        {healthProfileError && !healthProfileLoading && <ErrorBox error={healthProfileError} />}
        {!healthProfileError && !healthProfileLoading && (
          <Box className="health-profile-form-conditions-container">
            {sortedClinicalConditions.map((condition) => {
              return renderCondition(condition.label, healthProfile[condition.key] === true, () =>
                handleUpdateHealthProfile({
                  // Update value
                  [condition.key]: healthProfile[condition.key] !== true,
                })
              );
            })}
          </Box>
        )}
      </Box>
    </ContentPage>
  );
};
